import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { title: "", market: "", description: "", setup_rules: [""], checklist: [""], entry_rules: "", exit_rules: "", risk_rule: "", is_template: false, tier: "basic" };

function ListField({ label, items, onChange, testId }) {
  const { t } = useTranslation();
  const set = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{label}</label>
      <div className="mt-1 space-y-2">
        {items.map((x, i) => (
          <div key={i} className="flex gap-2">
            <Input value={x} onChange={(e) => set(i, e.target.value)} data-testid={`${testId}-${i}`} className="h-9" />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" aria-label="remove"><X size={16} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, ""])} data-testid={`${testId}-add`} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-800 hover:underline">
          <Plus size={12} /> {t("playbooks.addItem")}
        </button>
      </div>
    </div>
  );
}

export default function PlaybookEditor({ open, initial, isAdmin, onClose, onSave }) {
  const { t } = useTranslation();
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => { setSaving(true); try { await onSave(f); } finally { setSaving(false); } };
  const text = (k, rows = 2) => (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{t(`playbooks.f.${k}`)}</label>
      <Textarea rows={rows} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`playbook-editor-${k}`} className="mt-1" />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="playbook-editor">
        <DialogHeader><DialogTitle>{initial?.id ? t("playbooks.editTitle") : t("playbooks.newTitle")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{t("playbooks.f.title")}</label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="playbook-editor-title" className="mt-1" /></div>
          <div><label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{t("playbooks.f.market")}</label>
            <Input value={f.market} onChange={(e) => set("market", e.target.value)} placeholder="Gold · 15m" data-testid="playbook-editor-market" className="mt-1" /></div>
        </div>
        {text("description")}
        <ListField label={t("playbooks.f.setup_rules")} items={f.setup_rules} onChange={(v) => set("setup_rules", v)} testId="playbook-editor-rule" />
        <ListField label={t("playbooks.f.checklist")} items={f.checklist} onChange={(v) => set("checklist", v)} testId="playbook-editor-check" />
        {text("entry_rules")}{text("exit_rules")}{text("risk_rule", 1)}
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.is_template} disabled={!!initial?.id}
              onChange={(e) => set("is_template", e.target.checked)} data-testid="playbook-editor-template" /> {t("playbooks.asTemplate")}</label>
            {f.is_template && (
              <select value={f.tier} onChange={(e) => set("tier", e.target.value)} data-testid="playbook-editor-tier" className="h-8 rounded border border-slate-300 px-2">
                <option value="basic">Basic + Premium</option><option value="premium">Premium only</option>
              </select>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="playbook-editor-cancel">{t("playbooks.cancel")}</Button>
          <Button onClick={save} disabled={saving || !f.title.trim()} data-testid="playbook-editor-save" className="bg-blue-800 hover:bg-blue-700 text-white">{t("playbooks.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
